import { useLayout } from "@/app/providers";
import Icon from "../atoms/icon";
import Input from "../atoms/input";
import Text from "../atoms/text";
import View from "../atoms/view";
import cn from "@/utils/cn";

/**
 * @name SearchBar
 * @description Renders a search input field with a search icon button.
 *
 * @param {Object} props - Component props.
 * @param {(term: string) => void} props.handleSearch - Callback function invoked on search input change.
 * @param {string} [props.className] - Optional CSS classes.
 * @returns {JSX.Element} The rendered search bar component.
 */

export interface SearchBarProps {
  handleSearch: (term: string) => void;
  className?: string;
  placeholder?: string;
  iconPosition?: "left" | "right";
}

// Decorative search icon (not interactive)
const SearchIcon = (
  <Icon
    name="search:bx:bx"
    className="text-xl text-gray-400"
    aria-hidden="true"
  />
);

const SearchBar = ({
  handleSearch,
  className,
  placeholder = "",
  iconPosition = "right"
}: SearchBarProps) => {
  const { t } = useLayout();

  return (
    <View
      className={cn(
        "bg-primary/5 dark:bg-bg3 hidden md:flex gap-3 rounded-md border border-transparent focus-within:border-primary px-3 items-center justify-between w-full p-2",
        className
      )}
    >
      {iconPosition === "left" && SearchIcon}
      <Text type="label" htmlFor="search">
        {t("search")}
      </Text>
      <Input
        id="search"
        name="search"
        onChange={(e) => handleSearch(e.target.value)}
        placeholder={placeholder}
        className="bg-transparent py-2 text-sm focus:outline-none w-full"
      />
      {iconPosition === "right" && SearchIcon}
    </View>
  );
};

export default SearchBar;
